import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import Select from 'components/global/CUK/AsyncSelect/Select'
import { OptionMultiCount } from 'components/global/CUK/AsyncSelect/components/Option'
import { ValueLabel } from 'components/global/CUK/AsyncSelect/components/ValueContainer'

import { getSiteStateLabel } from '../constants'
import { OPERATION_AGG_SITES_UNSCOPED_STATES } from '../operations-gql/queries'
import { SitesUnscopeFilters, StateAgg } from '../types'

type AggSitesUnscopeStatesData = {
  states: StateAgg[]
}

type StateOption = {
  id: string
  value: string
  label: string
  count: number
}

type StateSelectProps = {
  filters: SitesUnscopeFilters
  onChange: (states: string[]) => void
}

const OptionStateCount = props => (
  <OptionMultiCount {...props} value={props.data} count={props.data.count} />
)

const StateValueContainer = props => {
  const { t } = useTranslation()
  const selectedCount = props.getValue().length
  const label = selectedCount === 0
    ? props.selectProps.placeholder
    : t('ui_elements.count_selected', { count: selectedCount })

  return <ValueLabel label={label} />
}

const StateSelect = ({ filters, onChange }: StateSelectProps) => {
  const { t } = useTranslation()
  const { state: selectedStates, verbatim, has_active_project, has_process, visibility_in_center } = filters

  const { data } = useQuery<AggSitesUnscopeStatesData>(OPERATION_AGG_SITES_UNSCOPED_STATES, {
    variables: { verbatim, has_active_project, has_process, visibility_in_center },
    fetchPolicy: 'cache-and-network'
  })

  const options = useMemo<StateOption[]>(() => {
    const states = data?.states ?? []
    const missingSelectedStates = selectedStates
      .filter(selectedState => !states.some(({ key }) => key === selectedState))
      .map(key => ({ key, count: 0 }))

    return [...states, ...missingSelectedStates].map(({ key, count }) => ({
      id: key,
      value: key,
      label: getSiteStateLabel(t, key),
      count
    }))
  }, [data?.states, selectedStates, t])

  const selectedOptions = options.filter(option => selectedStates.includes(option.value))

  const handleChange = (values: StateOption[]) => {
    onChange(values.map(({ value }) => value))
  }

  return (
    <Select
      /* @ts-expect-error JSX */
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      mainField="label"
      placeholder={t('admin_sites.filters.state')}
      onChange={handleChange}
      components={{ Option: OptionStateCount, ValueContainer: StateValueContainer }}
      value={selectedOptions}
    />
  )
}

export default StateSelect
